/**
 * Folders – search folders using searchFolders API.
 * Filter by repository, group category, folder type, folder name, folder number.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import Modal from '../../components/shared/Modal';
import ModalFormActions from '../../components/shared/ModalFormActions';
import { useActiveRepositories } from '../../hooks/edms/useActiveRepositories';
import { useActiveFolderTypes } from '../../hooks/edms/useActiveFolderTypes';
import { searchFolders, createFolder, getCategoryRootFolder } from '../../api/edms/folders';
import { Folder, Search, FolderPlus, FolderTree } from 'lucide-react';
import DataTable from '../../components/shared/DataTable';
import EmptyState from '../../components/shared/EmptyState';
import Pagination from '../../components/shared/Pagination';
import SuccessDialog from '../../components/shared/SuccessDialog';

const APP_ID = 'documents-management';
const APP_NAME = 'Documents Management';
const MODULE = 'Folders';

const GROUP_CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'Confidential', label: 'Confidential' },
  { value: 'General', label: 'General' },
  { value: 'Personal', label: 'Personal' },
];

const PERSONAL_SUBCATEGORIES = [
  { value: '', label: 'Select…' },
  { value: 'Staff', label: 'Staff' },
  { value: 'Member', label: 'Member' },
];

const EdmsFolders = () => {
  const navigate = useNavigate();
  const [repository, setRepository] = useState('');
  const [groupCategory, setGroupCategory] = useState('');
  const [folderTypeId, setFolderTypeId] = useState('');
  const [folderNameOrNumber, setFolderNameOrNumber] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [newFolderModal, setNewFolderModal] = useState(false);
  const [newFolderForm, setNewFolderForm] = useState({
    folderName: '',
    folderNumber: '',
    repository: '',
    groupCategory: '',
    groupSubCategory: '',
    folderTypeId: '',
    parentFolderId: '',
  });
  const [createFolderValidationError, setCreateFolderValidationError] = useState('');
  const [folderCreatedSuccess, setFolderCreatedSuccess] = useState({ isOpen: false, message: '' });

  const queryClient = useQueryClient();
  const { repositories } = useActiveRepositories({ enabled: true });
  const { folderTypes } = useActiveFolderTypes({ enabled: true });

  const parentFolderSearchParams =
    newFolderModal && newFolderForm.repository && newFolderForm.groupCategory
      ? {
          repository: newFolderForm.repository,
          groupCategory: newFolderForm.groupCategory,
          folderTypeId: newFolderForm.folderTypeId || undefined,
          page: 1,
          limit: 500,
        }
      : null;

  const { data: parentFoldersData } = useQuery({
    queryKey: ['edms', 'folders', 'parentList', parentFolderSearchParams],
    queryFn: () => searchFolders(parentFolderSearchParams || {}),
    enabled: !!parentFolderSearchParams,
  });

  const parentFoldersList = Array.isArray(parentFoldersData?.data)
    ? parentFoldersData.data
    : Array.isArray(parentFoldersData)
    ? parentFoldersData
    : parentFoldersData?.folders ?? [];

  const createFolderMutation = useMutation({
    mutationFn: createFolder,
    onSuccess: () => {
      setNewFolderModal(false);
      setCreateFolderValidationError('');
      setNewFolderForm({ folderName: '', folderNumber: '', repository: '', groupCategory: '', groupSubCategory: '', folderTypeId: '', parentFolderId: '' });
      queryClient.invalidateQueries({ queryKey: ['edms', 'folders', 'search'] });
      setFolderCreatedSuccess({ isOpen: true, message: 'Folder created successfully.' });
    },
    onError: () => {
      setCreateFolderValidationError('');
    },
  });

  const resetNewFolderForm = () => {
    setCreateFolderValidationError('');
    setNewFolderForm({ folderName: '', folderNumber: '', repository: '', groupCategory: '', groupSubCategory: '', folderTypeId: '', parentFolderId: '' });
  };

  const searchParams = submitted
    ? {
        repository: repository || undefined,
        groupCategory: groupCategory || undefined,
        folderTypeId: folderTypeId || undefined,
        q: folderNameOrNumber.trim() || undefined,
        page,
        limit,
      }
    : null;

  const { data: searchResult, isLoading, isError, error } = useQuery({
    queryKey: ['edms', 'folders', 'search', searchParams],
    queryFn: () => searchFolders(searchParams || {}),
    enabled: searchParams !== null,
  });

  const handleSearch = (e) => {
    e?.preventDefault();
    setSubmitted(true);
    setPage(1);
  };

  const rawPagination = searchResult?.pagination ?? searchResult?.data?.pagination;
  const pagination = rawPagination
    ? {
        currentPage: rawPagination.page ?? rawPagination.currentPage ?? 1,
        totalPages: (rawPagination.pages ?? rawPagination.totalPages ?? Math.ceil((rawPagination.total ?? 0) / (rawPagination.limit ?? limit))) || 1,
        hasPrevPage: (rawPagination.page ?? 1) > 1,
        hasNextPage: (rawPagination.page ?? 1) < (rawPagination.pages ?? rawPagination.totalPages ?? 1),
      }
    : null;

  const rows =
    Array.isArray(searchResult?.data)
      ? searchResult.data
      : Array.isArray(searchResult?.data?.folders)
      ? searchResult.data.folders
      : Array.isArray(searchResult?.data?.items)
      ? searchResult.data.items
      : Array.isArray(searchResult)
      ? searchResult
      : searchResult?.folders ?? [];

  const handleOpenInArchive = (folder) => {
    const id = folder._id ?? folder.id;
    if (!id) return;
    navigate(`/app/${APP_ID}/archive`, {
      state: {
        selectedFolderId: id,
        repository: folder.repository ?? folder.repo ?? undefined,
        groupCategory: folder.groupCategory ?? folder.category ?? undefined,
      },
    });
  };

  const columns = [
    {
      key: 'icon',
      header: '',
      headerClassName: 'w-12',
      className: 'w-12',
      render: (row) => (
        <Folder
          className="h-5 w-5 text-amber-500 shrink-0 inline-block"
          aria-label="Folder"
        />
      ),
    },
    {
      key: 'name',
      header: 'Folder name',
      render: (row) => (
        <span className="font-bold text-slate-800">
          {row.name ?? row.folderName ?? row.label ?? '—'}
        </span>
      ),
    },
    {
      key: 'number',
      header: 'Folder number',
      render: (row) => (
        <span className="text-slate-600">{row.folderNumber ?? row.number ?? '—'}</span>
      ),
    },
    {
      key: 'type',
      header: 'Folder type',
      render: (row) => (
        <span className="text-slate-600">
          {row.folderType?.name ?? row.folderTypeName ?? '—'}
        </span>
      ),
    },
    {
      key: 'groupCategory',
      header: 'Group category',
      render: (row) => (
        <span className="text-slate-600">{row.groupCategory ?? row.category ?? '—'}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => {
        const raw = row.createdAt ?? row.created_at;
        return <span className="text-slate-500 text-xs">{raw ? new Date(raw).toLocaleString() : '—'}</span>;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <span className="group relative inline-flex">
          <button
            type="button"
            onClick={() => handleOpenInArchive(row)}
            title="Open in Archive"
            className="inline-flex items-center justify-center rounded bg-white p-1 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
            aria-label="Open in Archive"
          >
            <FolderTree className="h-4 w-4 shrink-0 text-[var(--color-primary)]" strokeWidth={2.5} aria-hidden />
          </button>
          <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white opacity-0 shadow transition-opacity duration-150 group-hover:opacity-100">
            Open in Archive
          </span>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={`${APP_NAME} | ${MODULE}`}
        breadcrumbItems={[
          { label: APP_NAME, href: `/app/${APP_ID}` },
          { label: MODULE },
        ]}
      />
      <div className="rounded-2xl border border-cream/60 bg-white shadow-sm overflow-hidden">
        <div className="p-5 border-b border-cream/60">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">{MODULE}</h2>
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-4">
            <div className="min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Repository</label>
              <select
                value={repository}
                onChange={(e) => setRepository(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                aria-label="Filter by repository"
              >
                <option value="">All</option>
                {repositories?.map((r) => (
                  <option key={r._id ?? r.name} value={r.name ?? ''}>
                    {r.name ?? r.pathSegment ?? r._id}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Group Category</label>
              <select
                value={groupCategory}
                onChange={(e) => setGroupCategory(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                aria-label="Filter by group category"
              >
                {GROUP_CATEGORIES.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Folder type</label>
              <select
                value={folderTypeId}
                onChange={(e) => setFolderTypeId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                aria-label="Filter by folder type"
              >
                <option value="">All</option>
                {folderTypes?.map((ft) => (
                  <option key={ft._id ?? ft.id} value={ft._id ?? ft.id}>
                    {ft.name ?? ft.code ?? ft._id}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[200px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Folder name or number</label>
              <input
                type="text"
                value={folderNameOrNumber}
                onChange={(e) => setFolderNameOrNumber(e.target.value)}
                placeholder="Search by name or number..."
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                aria-label="Folder name or number"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <Search className="h-4 w-4" aria-hidden />
              Search
            </button>
            <button
              type="button"
              onClick={() => { setNewFolderModal(true); }}
              title="New folder"
              className="inline-flex items-center justify-center rounded-lg bg-primary p-2 hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/50"
              aria-label="New folder"
            >
              <FolderPlus className="h-4 w-4 text-[var(--color-accent-yellow)]" aria-hidden />
            </button>
          </form>
        </div>

        <div className="p-5">
          {!submitted && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Search className="h-12 w-12 text-slate-300 mb-3" aria-hidden />
              <p className="text-sm">Use the filters above and click Search to find folders.</p>
            </div>
          )}
          {submitted && (
            <>
              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <span className="text-sm text-slate-500">Searching…</span>
                </div>
              )}
              {isError && (
                <EmptyState
                  isError
                  title="Search failed"
                  description={error?.message ?? error?.response?.data?.message ?? 'Unable to search folders.'}
                />
              )}
              {!isLoading && !isError && (
                <>
                  {rows.length === 0 ? (
                    <EmptyState
                      title="No folders found"
                      description="Try different filters."
                      icon={<Folder className="h-12 w-12 text-slate-300" strokeWidth={1.25} />}
                    />
                  ) : (
                    <>
                      <DataTable
                        columns={columns}
                        rows={rows}
                        getRowKey={(row, i) => row._id ?? row.id ?? i}
                        emptyTitle="No results"
                        emptyMessage="No folders match your search."
                      />
                      {pagination && (
                        <div className="mt-3">
                          <Pagination pagination={pagination} onPageChange={(newPage) => setPage(newPage)} />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* New folder modal */}
      <Modal
        isOpen={newFolderModal}
        onClose={() => { setNewFolderModal(false); resetNewFolderForm(); }}
        title="Create new folder"
        size="sm"
      >
        <form
          id="new-folder-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setCreateFolderValidationError('');
            if (!newFolderForm.folderName.trim()) {
              setCreateFolderValidationError('Folder name is required.');
              return;
            }
            if (!newFolderForm.repository) {
              setCreateFolderValidationError('Repository is required.');
              return;
            }
            if (!newFolderForm.groupCategory) {
              setCreateFolderValidationError('Group category is required.');
              return;
            }
            if (newFolderForm.groupCategory === 'Personal' && !newFolderForm.groupSubCategory) {
              setCreateFolderValidationError('Under (Personal) is required when category is Personal.');
              return;
            }
            const payload = {
              folderName: newFolderForm.folderName.trim(),
              folderNumber: newFolderForm.folderNumber.trim() || '',
              repository: newFolderForm.repository,
              groupCategory: newFolderForm.groupCategory,
              folderTypeId: newFolderForm.folderTypeId || '',
            };
            if (newFolderForm.groupCategory === 'Personal') {
              payload.groupSubCategory = newFolderForm.groupSubCategory.trim() || '';
            }
            if (newFolderForm.parentFolderId) {
              payload.parentFolderId = newFolderForm.parentFolderId;
            } else if (['Confidential', 'General', 'Personal'].includes(newFolderForm.groupCategory)) {
              const root = await getCategoryRootFolder(newFolderForm.repository, newFolderForm.groupCategory, newFolderForm.groupSubCategory?.trim() || undefined);
              if (root?._id) payload.parentFolderId = root._id;
            }
            createFolderMutation.mutate(payload);
          }}
        >
          {(createFolderValidationError || createFolderMutation.isError) && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {createFolderValidationError ||
                createFolderMutation.error?.response?.data?.message ||
                createFolderMutation.error?.message ||
                'Failed to create folder.'}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Group category *</label>
              <select
                value={newFolderForm.groupCategory}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, groupCategory: e.target.value, parentFolderId: '' }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">Select category</option>
                {GROUP_CATEGORIES.filter((c) => c.value).map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            {newFolderForm.groupCategory === 'Personal' && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Under (Personal) *</label>
                <select
                  value={newFolderForm.groupSubCategory}
                  onChange={(e) => setNewFolderForm((f) => ({ ...f, groupSubCategory: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                >
                  {PERSONAL_SUBCATEGORIES.map((o) => (
                    <option key={o.value || 'blank'} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Repository *</label>
              <select
                value={newFolderForm.repository}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, repository: e.target.value, parentFolderId: '' }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">Select repository</option>
                {repositories?.map((r) => (
                  <option key={r._id ?? r.name} value={r.name ?? ''}>
                    {r.name ?? r.pathSegment ?? r._id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder type</label>
              <select
                value={newFolderForm.folderTypeId}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, folderTypeId: e.target.value, parentFolderId: '' }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">—</option>
                {folderTypes?.map((ft) => (
                  <option key={ft._id ?? ft.id} value={ft._id ?? ft.id}>
                    {ft.name ?? ft.code ?? ft._id}
                  </option>
                ))}
              </select>
            </div>
            {newFolderForm.repository && newFolderForm.groupCategory && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Parent folder</label>
                <select
                  value={newFolderForm.parentFolderId}
                  onChange={(e) => setNewFolderForm((f) => ({ ...f, parentFolderId: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                >
                  <option value="">
                    — Under {newFolderForm.groupCategory} (root)
                  </option>
                  {parentFoldersList.map((pf) => (
                    <option key={pf._id ?? pf.id} value={pf._id ?? pf.id}>
                      {pf.name ?? pf.folderName ?? pf.label ?? pf.folderNumber ?? pf._id}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Filtered by category and folder type. Empty list means create under {newFolderForm.groupCategory}.
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder name *</label>
              <input
                type="text"
                value={newFolderForm.folderName}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, folderName: e.target.value }))}
                placeholder="Enter folder name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder number</label>
              <input
                type="text"
                value={newFolderForm.folderNumber}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, folderNumber: e.target.value }))}
                placeholder="Optional"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            </div>
          </div>
        </form>
        <div className="mt-4 flex justify-end gap-2">
          <ModalFormActions
            formId="new-folder-form"
            onCancel={() => { setNewFolderModal(false); resetNewFolderForm(); }}
            isSubmitting={createFolderMutation.isPending}
            submitText="Create"
            submittingText="Creating…"
          />
        </div>
      </Modal>

      <SuccessDialog
        isOpen={folderCreatedSuccess.isOpen}
        title="Success"
        message={folderCreatedSuccess.message}
        onClose={() => setFolderCreatedSuccess({ isOpen: false, message: '' })}
        variant="success"
      />
    </>
  );
};

export default EdmsFolders;
